/**
 * @file: util.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { ReportNavParams } from '@euler/app/flows/report/ReportScreens';
import {
  ReportDocType,
  VehicleReport,
  VehicleReportProjection,
} from '@euler/model/report';
import { ReportTabKey } from '@euler/model/viewmodel';
import { assertNever } from '@euler/utils';

export const projectionForReportTab = (
  tab: ReportTabKey | undefined,
): VehicleReportProjection => {
  if (tab == null) {
    return {
      preInspection: true,
      inspection: true,
      obdInspection: true,
      construction: true,
      deliveryCheck: true,
      samplingCheck: true,
    };
  }
  switch (tab) {
    case 'preInspection': {
      return {
        preInspection: true,
      };
    }
    case 'inspection': {
      return {
        inspection: true,
        obdInspection: true,
      };
    }
    case 'construction': {
      return { construction: true };
    }
    case 'deliveryCheck': {
      return { deliveryCheck: true };
    }
    case 'samplingCheck': {
      return { samplingCheck: true };
    }
    default: {
      assertNever(tab, 'Unsupported report tab key:' + tab);
    }
  }
};

export function isPreInspectionReportEmpty(report: VehicleReport) {
  return (
    report.preInspection == null ||
    (report.preInspection.items.length === 0 && !report.dashboardImgUrl)
  );
}

export function isInspectionReportEmpty(report: VehicleReport) {
  return !(
    report.inspection?.items.length ??
    report.obdInspection?.troubleCodes?.length
  );
}

export function isConstructionReportEmpty(report: VehicleReport) {
  return !report.construction?.items.length;
}

export function isDeliveryCheckReportEmpty(report: VehicleReport) {
  return !report.deliveryCheck?.items.length;
}

export function isSamplingCheckReportEmpty(report: VehicleReport) {
  return !report.samplingCheck?.items.length;
}

export function isReportTabContentEmpty(
  report: VehicleReport,
  tab: ReportTabKey,
) {
  switch (tab) {
    case 'preInspection':
      return isPreInspectionReportEmpty(report);
    case 'inspection':
      return isInspectionReportEmpty(report);
    case 'construction':
      return isConstructionReportEmpty(report);
    case 'deliveryCheck':
      return isDeliveryCheckReportEmpty(report);
    case 'samplingCheck':
      return isSamplingCheckReportEmpty(report);
    default:
      assertNever(tab, 'Unsupported report tab key:' + tab);
  }
}

export function reportTabKeyFromScreen(
  screen: keyof ReportNavParams,
): ReportTabKey {
  switch (screen) {
    case 'preInspectionReport':
      return 'preInspection';
    case 'inspectionReport':
      return 'inspection';
    case 'constructionReport':
      return 'construction';
    case 'deliveryCheckReport':
      return 'deliveryCheck';
    case 'samplingCheckReport':
      return 'samplingCheck';
  }
}

export function reportScreenFromTabKey(
  tab: ReportTabKey,
): keyof ReportNavParams {
  switch (tab) {
    case 'preInspection':
      return 'preInspectionReport';
    case 'inspection':
      return 'inspectionReport';
    case 'construction':
      return 'constructionReport';
    case 'deliveryCheck':
      return 'deliveryCheckReport';
    case 'samplingCheck':
      return 'samplingCheckReport';
  }
}

export function reportDocTypeFromScreen(
  screen: keyof ReportNavParams,
): ReportDocType {
  switch (screen) {
    case 'preInspectionReport':
      return 'preInspection';
    case 'inspectionReport':
      return 'inspection';
    case 'constructionReport':
      return 'construction';
    case 'deliveryCheckReport':
      return 'deliveryCheck';
    case 'samplingCheckReport':
      return 'samplingCheck';
  }
}
